import { Component, inject, PLATFORM_ID, AfterViewInit, OnDestroy, ViewChild, ElementRef, signal, DestroyRef } from '@angular/core';
import { Navbar } from '../../shared/components/layout/navbar/navbar';
import { Footer } from '../../shared/components/layout/footer/footer';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Masks} from '../../shared/utils/masks';
import { MaskitoDirective } from '@maskito/angular';
import { InputTextModule } from 'primeng/inputtext';
import { isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { SeoManager } from '../../core/services/seo/seo-manager';
import { ButtonModule } from 'primeng/button';
import { Observable, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemService } from '../../core/services/system.service';
import { MessageService } from 'primeng/api';

export type contactType = 'product' | 'pqr';

@Component({
  selector: 'app-contact',
  imports: [Navbar, Footer, ReactiveFormsModule, FormsModule, CommonModule, MaskitoDirective, InputTextModule, ButtonModule],
  templateUrl: './contact.html',
  styleUrl: './contact.scss',
})
export class Contact implements AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private scrollSmoother: ScrollSmoother | null = null;
  private seoManager = inject(SeoManager);
  private systemService = inject(SystemService);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);

  @ViewChild('smoothWrapper', { static: false }) smoothWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('smoothContent', { static: false }) smoothContent!: ElementRef<HTMLDivElement>;

  masks = Masks;

  contactForm!: FormGroup;

  contact_type: contactType = 'product';

  loading$ = signal(false);

  private subscribeToLoading(loading$: Observable<boolean>) {
    loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => {
        this.loading$.set(loading);
      });
  }

  constructor() {
    this.seoManager.updateSeoTags({
      title: 'Contacto',
      description: 'Contacte con nosotros para obtener más información sobre nuestro producto.',
    });

    this.contactForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      message: new FormControl('', [Validators.required]),
      organization_name: new FormControl('', [Validators.required]),
      users_qt: new FormControl('', [Validators.required]),
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
      this.initScrollSmoother();
    }
  }

  ngOnDestroy() {
    if (this.scrollSmoother) {
      this.scrollSmoother.kill();
    }
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }

  private initScrollSmoother() {
    if (!this.smoothWrapper?.nativeElement || !this.smoothContent?.nativeElement) {
      return;
    }

    this.scrollSmoother = ScrollSmoother.create({
      wrapper: this.smoothWrapper.nativeElement,
      content: this.smoothContent.nativeElement,
      smooth: 1.5,
      effects: true,
      smoothTouch: 0.1,
    });
  }

  selectContactType(contactType: contactType) {
    this.contact_type = contactType;
    this.contactForm.patchValue({
      contact_type: contactType
    });
    if (this.contactForm) {
      this.contactForm.reset();
      if (contactType === 'product') {
        if(!this.contactForm.contains('organization_name') && !this.contactForm.contains('users_qt')) {
          this.contactForm.addControl('organization_name', new FormControl('', [Validators.required]));
          this.contactForm.addControl('users_qt', new FormControl('', [Validators.required]));
        }
      } else {
        this.contactForm.removeControl('organization_name');
        this.contactForm.removeControl('users_qt');
      }
    }

  }

  submitContact() {
    this.contactForm.value.contact_type = this.contact_type;
    const response = this.systemService.submitContact(this.contactForm.value);
    this.subscribeToLoading(response.loading$);
    response.request$.pipe(take(1)).subscribe({
      next: (response) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Formulario enviado correctamente' });
        this.contactForm.reset();
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error al enviar el contacto, intente nuevamente' });
      }
    });
  }
}
